import { useState } from 'react';
import { api, type Server } from '../../api';
import { useResource } from '../../hooks';
import { Section } from '../primitives';
import { ConfirmDialog, ErrorNotice } from '../ui';
import { relativeTime } from '../project/projectHealth';

/**
 * ServerCloudSection (CO-200): the Hetzner server behind a server that was
 * created from Add server. It is billed to the organization's own Hetzner
 * account until it is deleted there, so deleting it is one step with the
 * server's name typed, and removing the server from Commerce Ops is refused
 * while it still runs.
 */
export function ServerCloudSection({ server }: { server: Server }) {
  const [reload, setReload] = useState(0);
  const cloud = useResource(() => api.getServerCloudInstance(server.id), [server.id, reload], undefined, { key: `server:${server.id}:cloud-instance` });
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const instance = cloud.data?.cloud ? cloud.data.instance : undefined;
  if (!instance) return null;

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.deleteServerCloudInstance(server.id);
      setConfirm(false);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err as Error);
      setConfirm(false);
    } finally {
      setBusy(false);
    }
  }

  const deleted = Boolean(instance.deleted_at);
  return (
    <Section
      title="Hetzner server"
      testId="server-cloud"
      actions={
        deleted ? null : (
          <button type="button" className="button button--danger-quiet button--small" onClick={() => setConfirm(true)} data-testid="cloud-delete">
            Delete on Hetzner
          </button>
        )
      }
    >
      <ErrorNotice error={error} />
      <dl className="settings-facts" aria-label="Hetzner server">
        <div>
          <dt>Status</dt>
          <dd>{deleted ? `Deleted ${relativeTime(instance.deleted_at!)}` : 'Running'}</dd>
        </div>
        <div>
          <dt>Location</dt>
          <dd>{instance.location}</dd>
        </div>
        <div>
          <dt>Size</dt>
          <dd>{instance.server_type}</dd>
        </div>
        {instance.public_ip ? (
          <div>
            <dt>Address</dt>
            <dd>
              <code translate="no">{instance.public_ip}</code>
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Created</dt>
          <dd>{relativeTime(instance.created_at)}</dd>
        </div>
      </dl>
      {confirm ? (
        <ConfirmDialog
          open={true}
          title={`Delete ${server.name} on Hetzner?`}
          description="Deletes the server and its data. No undo."
          confirmLabel="Delete on Hetzner"
          confirmText={server.name}
          danger={true}
          busy={busy}
          onConfirm={remove}
          onCancel={() => setConfirm(false)}
        />
      ) : null}
    </Section>
  );
}
